import type { BlogPost } from '@/types/home'
import { PATHS } from '@/routes/paths'
import { absoluteUrl, cleanSeoText, titleCase, truncateMetaDescription, uniqueKeywords } from '../utils'
import { breadcrumbSchema } from '../schemas/common'

export function buildBlogListSeo() {
  return {
    title: 'Real Estate Blog & Property Insights | Quick Build Con',
    description: truncateMetaDescription(
      'Read expert articles on buying, selling and renting property in India. Market trends, guides, locality insights and investment tips from Quick Build Con.',
    ),
    keywords: uniqueKeywords([
      'real estate blog',
      'property news india',
      'home buying tips',
      'qbc blog',
      'property guides',
    ]),
    canonical: absoluteUrl(PATHS.blog),
  }
}

export function buildBlogCategorySeo(slug: string, name?: string) {
  const label = name || titleCase(slug) || 'Category'
  return {
    title: `${label} Articles | QBC Blog`,
    description: truncateMetaDescription(
      `Explore ${label} articles on property, real estate trends and home buying advice from Quick Build Con.`,
    ),
    keywords: uniqueKeywords([`${label.toLowerCase()} blog`, 'real estate articles', 'qbc blog']),
    canonical: absoluteUrl(PATHS.blogCategory(slug)),
  }
}

export function buildBlogDetailSeo(post: BlogPost & { content?: string; tags?: string[] }) {
  const description = truncateMetaDescription(post.excerpt || cleanSeoText(post.content || post.title))
  const canonical = absoluteUrl(PATHS.blogDetail(post.slug))
  const authorName = (post.author || '').trim() || 'QBC Team'
  const published = post.publishedAt || undefined

  return {
    title: `${post.title} | QBC Blog`,
    description,
    keywords: uniqueKeywords([
      post.category,
      ...(post.tags ?? []),
      'real estate blog',
      'Quick Build Con',
    ]),
    canonical,
    ogImage: post.image || undefined,
    ogType: 'article',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description,
        image: post.image ? [post.image] : undefined,
        datePublished: published,
        dateModified: published,
        author: { '@type': 'Person', name: authorName },
        publisher: {
          '@type': 'Organization',
          name: 'Quick Build Con',
          logo: {
            '@type': 'ImageObject',
            url: absoluteUrl('/logoqbc.webp'),
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonical,
        },
        ...(post.readingTime
          ? { timeRequired: `PT${Math.max(1, Number(post.readingTime) || 1)}M` }
          : {}),
      },
      breadcrumbSchema([
        { name: 'Home', url: PATHS.home },
        { name: 'Blog', url: PATHS.blog },
        { name: post.title, url: PATHS.blogDetail(post.slug) },
      ]),
    ],
  }
}
